import { CLARITY_DOCUMENT_LIMITS } from './constants/clarity-document-limits.constant';

/** A provider error naming a position inside the request body, e.g. `messages.0.content.1.source`. */
const CONTENT_PATH = /messages\.\d+\.content\.\d+/i;
const MENTIONS_PDF = /pdf/i;
const MENTIONS_PAGE = /page/i;
const MENTIONS_ATTACHMENT = /pdf|document|file/i;
const EXPLICIT_PAGE_LIMIT = /too many pages|exceeds.*page.*limit/i;

/**
 * The page-limit refusal, built from the one definition of the number so it cannot drift — which is
 * exactly what `PDF_PAGE_LIMIT_MESSAGE` in `shared/utils/errors.ts` is for upstream. The wording is
 * theirs, to the character.
 */
const PAGE_LIMIT_MESSAGE =
	`This PDF exceeds the maximum page limit (${CLARITY_DOCUMENT_LIMITS.PDF_MAX_PAGES} pages). ` +
	'Please upload a shorter document or split it into parts.';
const ATTACHMENT_MESSAGE =
	'There was a problem processing your uploaded file. ' +
	'The file may be too large, corrupted, or in an unsupported format.';
const CONTENT_MESSAGE =
	'There was a problem processing your message content. Please try again or simplify your attachments.';

/**
 * Turn a raw provider error message into text worth showing a user.
 *
 * The upstream model API reports attachment problems as a path into the request body
 * (`messages.0.content.1.source.data`), which is accurate and unreadable. This maps the three cases
 * that have a useful thing to say and passes everything else through unchanged — a message this
 * function does not recognise is still better than a generic apology.
 *
 * Pass-through is deliberate but not unconditional safety: the caller decides where the result is
 * rendered. Anything that reaches `[innerHTML]` must still go through `htmlMessage`.
 *
 * Ported from `formatApiError` in Clarity `apps/web/src/shared/utils/errors.ts`. The `ApiError` class
 * and `isApiError` guard beside it were **not** ported — `ClarityApiError` in
 * `@efficiently/client-core-clarity-http` already carries the status code and is what this
 * workspace throws.
 */
export function formatClarityApiMessage(message: string): string {
	if (!message) {
		return message;
	}

	if (CONTENT_PATH.test(message)) {
		if (MENTIONS_PDF.test(message) && MENTIONS_PAGE.test(message)) {
			return PAGE_LIMIT_MESSAGE;
		}

		if (MENTIONS_ATTACHMENT.test(message)) {
			return ATTACHMENT_MESSAGE;
		}

		return CONTENT_MESSAGE;
	}

	if (EXPLICIT_PAGE_LIMIT.test(message)) {
		return PAGE_LIMIT_MESSAGE;
	}

	return message;
}
